import {
    currencyFormat,
    formatCentsToDisplay,
    parseCurrencyToCents,
} from '@/lib/form-state/money'
import { OnboardingSpecialty } from '@/lib/api/onboarding/onboarding-api'
import { markupPercent, profitMarginPercent } from '../onboarding-state'

interface Props {
    value: OnboardingSpecialty[]
    /** Custo de UMA consulta (custo mensal já rateado), em centavos. */
    cost: number
    onChange: (specialties: OnboardingSpecialty[]) => void
}

/**
 * Valor da consulta por especialidade, com o custo já rateado pela etapa anterior.
 *
 * Markup e margem aparecem juntos porque são coisas diferentes sobre a mesma
 * venda: **markup é sobre o CUSTO** (`(venda − custo) / custo`) e **margem é sobre
 * a VENDA** (`(venda − custo) / venda`). 100% de markup é 50% de margem — mostrar
 * só um dos dois faz o médico ler o número errado.
 */
export default function PricingStep({ value, cost, onChange }: Props) {
    const consulted = value
        .map((specialty, index) => ({ specialty, index }))
        .filter(({ specialty }) => specialty.consults !== false)

    const setPrice = (index: number, raw: string) => {
        const cents = parseCurrencyToCents(currencyFormat(raw, true))
        onChange(
            value.map((specialty, i) =>
                i === index ? { ...specialty, consultationPrice: cents } : specialty
            )
        )
    }

    const setMaxDiscount = (index: number, raw: string) => {
        const parsed = parseInt(raw.replace(/\D/g, ''), 10)
        onChange(
            value.map((specialty, i) =>
                i === index
                    ? {
                          ...specialty,
                          maxDiscount: Number.isFinite(parsed)
                              ? Math.min(parsed, 100)
                              : undefined,
                      }
                    : specialty
            )
        )
    }

    const percentLabel = (percent: number | null): string =>
        percent === null ? '—' : `${percent.toFixed(0)}%`

    if (consulted.length === 0) {
        return (
            <div className="alert alert-warning text-sm">
                <span className="whitespace-normal">
                    Nenhuma especialidade marcada como consulta. Volte uma etapa e marque
                    ao menos uma, ou siga: você pode cadastrar as consultas depois, no
                    catálogo.
                </span>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-5">
            <p className="text-base-content/70">
                O custo veio da etapa anterior:{' '}
                <strong>{formatCentsToDisplay(cost) || 'R$ 0,00'}</strong> por consulta.
                Informe quanto você cobra.
            </p>

            <ul className="flex flex-col gap-4">
                {consulted.map(({ specialty, index }) => {
                    const price = specialty.consultationPrice ?? 0
                    const markup = markupPercent(cost, price)
                    const margin = price ? profitMarginPercent(cost, price) : null
                    const negative = margin !== null && margin < 0

                    return (
                        <li
                            key={`${specialty.name}-${index}`}
                            className="rounded-lg border border-base-300 p-4"
                        >
                            <p className="font-medium text-base-content mb-3">
                                {specialty.name}
                            </p>

                            <div className="grid gap-4 md:grid-cols-3">
                                <div className="form-control min-w-0">
                                    <label className="label pb-1">
                                        <span className="label-text font-medium">
                                            Custo da consulta
                                        </span>
                                    </label>
                                    <input
                                        className="input input-bordered w-full"
                                        value={formatCentsToDisplay(cost) || 'R$ 0,00'}
                                        readOnly
                                        disabled
                                    />
                                </div>

                                <div className="form-control min-w-0">
                                    <label
                                        className="label pb-1"
                                        htmlFor={`onboarding-price-${index}`}
                                    >
                                        <span className="label-text font-medium">
                                            Valor cobrado
                                        </span>
                                    </label>
                                    <input
                                        id={`onboarding-price-${index}`}
                                        className="input input-bordered w-full"
                                        value={formatCentsToDisplay(price)}
                                        onChange={e => setPrice(index, e.target.value)}
                                        placeholder="R$ 0,00"
                                        inputMode="numeric"
                                    />
                                </div>

                                <div className="form-control min-w-0">
                                    <label
                                        className="label pb-1"
                                        htmlFor={`onboarding-discount-${index}`}
                                    >
                                        <span className="label-text font-medium">
                                            Desconto máximo (%)
                                        </span>
                                    </label>
                                    <input
                                        id={`onboarding-discount-${index}`}
                                        className="input input-bordered w-full"
                                        value={specialty.maxDiscount ?? ''}
                                        onChange={e => setMaxDiscount(index, e.target.value)}
                                        placeholder="0"
                                        inputMode="numeric"
                                    />
                                    <p className="mt-1 text-xs text-base-content/60 whitespace-normal break-words">
                                        Em branco, a recepção não pode dar desconto.
                                    </p>
                                </div>
                            </div>

                            <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 border-t border-base-200 pt-3">
                                <span className="text-sm text-base-content/60">
                                    Markup sobre o custo:{' '}
                                    <strong className="text-base-content">
                                        {percentLabel(markup)}
                                    </strong>
                                </span>
                                <span className="text-sm text-base-content/60">
                                    Margem de lucro:{' '}
                                    <strong
                                        className={
                                            negative ? 'text-error' : 'text-base-content'
                                        }
                                    >
                                        {percentLabel(margin)}
                                    </strong>
                                </span>
                                {negative && (
                                    <span className="text-sm text-error whitespace-normal">
                                        O valor cobrado está abaixo do custo.
                                    </span>
                                )}
                            </div>
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}
